import React, { useMemo } from 'react';
import { useCachedFxRange } from '../../hooks/useCachedData';
import { parseResultValue } from '../../util/GenericUtil';

function TestBoxplot({ testData, markerContribution, diagnosticKnots, diagnosisName, showDetails = false, className = '' }) {
    const { getFxRange } = useCachedFxRange();

    const plotData = useMemo(() => {
        if (!testData) return null;

        const testName = testData.alias || testData.test_name;
        const result = parseResultValue(testData.result);
        const labLow = parseFloat(testData.lab_low);
        const labHigh = parseFloat(testData.lab_high);
        const units = testData.units;

        // Get functional range
        const fxRange = getFxRange(testName, 'male'); // Default to male
        const fxLow = fxRange ? parseFloat(fxRange.fx_low) : null;
        const fxHigh = fxRange ? parseFloat(fxRange.fx_high) : null;

        // Calculate plot dimensions
        const allValues = [result, labLow, labHigh];
        if (fxLow !== null) allValues.push(fxLow);
        if (fxHigh !== null) allValues.push(fxHigh);

        const minVal = Math.min(...allValues) * 0.8;
        const maxVal = Math.max(...allValues) * 1.2;
        const range = maxVal - minVal;

        // Convert to percentages for positioning
        const resultPos = ((result - minVal) / range) * 100;
        const labLowPos = ((labLow - minVal) / range) * 100;
        const labHighPos = ((labHigh - minVal) / range) * 100;
        const fxLowPos = fxLow !== null ? ((fxLow - minVal) / range) * 100 : null;
        const fxHighPos = fxHigh !== null ? ((fxHigh - minVal) / range) * 100 : null;

        // Process diagnostic knots for display
        const knotPositions = diagnosticKnots ? diagnosticKnots.map(knot => ({
            x: knot.x,
            y: knot.y,
            xPos: ((knot.x - minVal) / range) * 100
        })).filter(knot => knot.xPos >= 0 && knot.xPos <= 100) : [];

        return {
            testName,
            result,
            units,
            resultPos,
            labLow,
            labHigh,
            labLowPos,
            labHighPos,
            fxLow,
            fxHigh,
            fxLowPos,
            fxHighPos,
            minVal,
            maxVal,
            range,
            knotPositions
        };
    }, [testData, getFxRange, diagnosticKnots]);

    if (!plotData) return null;

    const getStatusClass = () => {
        const { result, labLow, labHigh, fxLow, fxHigh } = plotData;

        if (result < labLow || result > labHigh) return 'abnormal';
        if (fxLow !== null && fxHigh !== null && (result < fxLow || result > fxHigh)) return 'suboptimal';
        return 'normal';
    };

    return (
        <div className={`test-boxplot ${className} ${getStatusClass()}`}>
            <div className="boxplot-header">
                <div className="test-info">
                    <span className="test-name">{plotData.testName}</span>
                    <span className="test-value">
                        {plotData.result} {plotData.units}
                        {markerContribution && (
                            <span className="contribution">
                                (contrib: {markerContribution.toFixed(3)})
                            </span>
                        )}
                    </span>
                </div>
            </div>

            <div className="boxplot-container">
                <div className="plot-area">
                    {/* Reference Range Bar */}
                    <div
                        className="range-bar reference-range"
                        style={{
                            left: `${plotData.labLowPos}%`,
                            width: `${plotData.labHighPos - plotData.labLowPos}%`
                        }}
                        title={`Reference Range: ${plotData.labLow}-${plotData.labHigh} ${plotData.units}`}
                    >
                        <span className="range-label">Lab Range</span>
                    </div>

                    {/* Ideal Range Bar */}
                    {plotData.fxLowPos !== null && plotData.fxHighPos !== null && (
                        <div
                            className="range-bar functional-range"
                            style={{
                                left: `${plotData.fxLowPos}%`,
                                width: `${plotData.fxHighPos - plotData.fxLowPos}%`
                            }}
                            title={`Ideal Range: ${plotData.fxLow}-${plotData.fxHigh} ${plotData.units}`}
                        >
                            <span className="range-label">Ideal</span>
                        </div>
                    )}

                    {/* Result Marker */}
                    <div
                        className="result-marker"
                        style={{ left: `${plotData.resultPos}%` }}
                        title={`Result: ${plotData.result} ${plotData.units}`}
                    >
                        <div className="marker-line"></div>
                        <div className="marker-dot"></div>
                    </div>

                    {/* Diagnostic Knots */}
                    {plotData.knotPositions && plotData.knotPositions.length > 0 && (
                        <div className="diagnostic-knots">
                            {plotData.knotPositions.map((knot, index) => (
                                <div
                                    key={index}
                                    className="diagnostic-knot"
                                    style={{
                                        left: `${knot.xPos}%`,
                                        height: `${Math.max(2, Math.abs(knot.y) * 20)}px`
                                    }}
                                    title={`${diagnosisName || 'Diagnosis'}: x=${knot.x.toFixed(1)}, weight=${knot.y.toFixed(3)}`}
                                >
                                    <div className={`knot-marker ${knot.y >= 0 ? 'positive' : 'negative'}`}></div>
                                    <div className="knot-weight">{knot.y.toFixed(2)}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Scale */}
                    <div className="scale">
                        <span className="scale-min">{plotData.labLow}</span>
                        <span className="scale-max">{plotData.labHigh}</span>
                    </div>
                </div>

                {showDetails && (
                    <div className="boxplot-details">
                        <div className="detail-row">
                            <span>Lab Range:</span>
                            <span>{plotData.labLow} - {plotData.labHigh} {plotData.units}</span>
                        </div>
                        {plotData.fxLow !== null && plotData.fxHigh !== null && (
                            <div className="detail-row">
                                <span>Ideal Range:</span>
                                <span>{plotData.fxLow} - {plotData.fxHigh} {plotData.units}</span>
                            </div>
                        )}
                        <div className="detail-row">
                            <span>Status:</span>
                            <span className={`status ${getStatusClass()}`}>
                                {getStatusClass().charAt(0).toUpperCase() + getStatusClass().slice(1)}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TestBoxplot;